import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  AlertCircle,
  ArrowLeft,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  Loader2,
  MapPin,
  Package,
  Search,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { Input } from "~/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useToast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import { pickingService } from "~/services/wms/pickingService";
import { useProfileStore } from "~/store/profileStore";
import type { PickingOrder, PickingOrdersByClient } from "~/types/wms";

import { PickingTaskAssignmentPanel } from "./components/PickingTaskAssignmentPanel";

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(amount);
}

function StockBadge({ available }: { available: boolean }) {
  if (available) {
    return (
      <Badge
        variant="outline"
        className="border-green-200 bg-green-50 text-green-700"
      >
        <CheckCircle2 className="mr-1 h-3 w-3" />
        Stock OK
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
      <AlertCircle className="mr-1 h-3 w-3" />
      Sin Stock
    </Badge>
  );
}

function PickTaskStatusBadge({ status }: { status?: string }) {
  if (!status) return null;

  const config: Record<string, { label: string; className: string }> = {
    pending: { label: "Pendiente", className: "bg-gray-100 text-gray-700" },
    assigned: { label: "Asignado", className: "bg-blue-100 text-blue-700" },
    in_progress: {
      label: "En Progreso",
      className: "bg-yellow-100 text-yellow-700",
    },
    completed: {
      label: "Completado",
      className: "bg-green-100 text-green-700",
    },
  };

  const c = config[status] || config.pending;
  return <Badge className={c.className}>{c.label}</Badge>;
}

interface OrderCardProps {
  order: PickingOrder;
  onStartPicking: (orderId: string) => void;
  onContinuePicking: (pickTaskId: string) => void;
  isStarting: boolean;
}

function OrderCard({
  order,
  onStartPicking,
  onContinuePicking,
  isStarting,
}: OrderCardProps) {
  const [showItems, setShowItems] = useState(false);
  const hasPickTask = !!order.pick_task_id;
  const isCompleted = order.pick_task_status === "completed";
  const isPending = order.pick_task_status === "pending";
  const isInProgress =
    order.pick_task_status === "in_progress" ||
    order.pick_task_status === "assigned";

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-sm font-medium">
                Pedido #{order.id.slice(0, 8)}
              </span>
              {order.delivery_date && (
                <Badge variant="outline" className="text-xs">
                  {dayjs(order.delivery_date).format("DD MMM YYYY")}
                </Badge>
              )}
              <StockBadge available={order.stock_available} />
              {hasPickTask && (
                <PickTaskStatusBadge status={order.pick_task_status} />
              )}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {order.items_count} productos |{" "}
              {formatCurrency(order.total_amount)}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!hasPickTask && (
              <Button
                size="sm"
                className="w-full sm:w-auto"
                disabled={!order.stock_available || isStarting}
                onClick={() => onStartPicking(order.id)}
              >
                {isStarting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Package className="mr-2 h-4 w-4" />
                )}
                Iniciar Picking
              </Button>
            )}
            {isPending && order.pick_task_id && (
              <Button
                size="sm"
                className="w-full sm:w-auto"
                disabled={!order.stock_available}
                onClick={() => onContinuePicking(order.pick_task_id!)}
              >
                <Package className="mr-2 h-4 w-4" />
                Iniciar Picking
              </Button>
            )}
            {isInProgress && order.pick_task_id && (
              <Button
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => onContinuePicking(order.pick_task_id!)}
              >
                <Package className="mr-2 h-4 w-4" />
                Continuar
              </Button>
            )}
            {isCompleted && (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Picking Completo
              </Badge>
            )}
          </div>
        </div>

        {/* Expandable items list */}
        <Collapsible open={showItems} onOpenChange={setShowItems}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full justify-start text-muted-foreground"
            >
              {showItems ? (
                <ChevronDown className="mr-2 h-4 w-4" />
              ) : (
                <ChevronRight className="mr-2 h-4 w-4" />
              )}
              Ver productos
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-1 rounded-md border p-2">
              {order.items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between gap-2 py-1 text-xs sm:text-sm"
                >
                  <span
                    className={cn(
                      "min-w-0 flex-1",
                      !item.sufficient && "text-red-600"
                    )}
                  >
                    {item.product_name}
                  </span>
                  <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                    <span className="whitespace-nowrap text-muted-foreground">
                      {item.quantity} cajas
                    </span>
                    {!item.sufficient && (
                      <span className="whitespace-nowrap text-xs text-red-500">
                        (disp: {item.stock_available})
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

interface ClientGroupProps {
  client: PickingOrdersByClient;
  onStartPicking: (orderId: string) => void;
  onContinuePicking: (pickTaskId: string) => void;
  isStarting: boolean;
}

function ClientGroup({
  client,
  onStartPicking,
  onContinuePicking,
  isStarting,
}: ClientGroupProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer px-4 py-3 transition-colors hover:bg-muted/50 sm:px-6 sm:py-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-start gap-2 sm:gap-3">
                {isOpen ? (
                  <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground sm:h-5 sm:w-5" />
                ) : (
                  <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground sm:h-5 sm:w-5" />
                )}
                <div className="min-w-0">
                  <CardTitle className="text-sm font-semibold leading-tight sm:text-base">
                    {client.client_name}
                  </CardTitle>
                  {client.delivery_address.full_address && (
                    <div className="mt-1 flex items-start gap-1 text-xs text-muted-foreground sm:text-sm">
                      <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                      <span className="line-clamp-2">
                        {client.delivery_address.full_address}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <Badge variant="secondary" className="shrink-0 text-xs">
                {client.orders.length} pedidos
              </Badge>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-3 px-3 pt-0 sm:px-6">
            {client.orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onStartPicking={onStartPicking}
                onContinuePicking={onContinuePicking}
                isStarting={isStarting}
              />
            ))}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// ─── Bodega worker view ───────────────────────────────────────────────────────

type WorkerTaskItem = {
  id: string;
  requested_quantity: number;
  product?: { short_name: string };
};

type WorkerTask = {
  id: string;
  status: string;
  sales_request?: {
    id: string;
    client?: { name: string };
    delivery_date?: string;
  };
  items?: WorkerTaskItem[];
};

function BodegaTaskCard({
  task,
  onStart,
}: {
  task: WorkerTask;
  onStart: () => void;
}) {
  const [open, setOpen] = useState(false);
  const sr = task.sales_request;
  const items = task.items ?? [];
  const totalBoxes = items.reduce(
    (sum, i) => sum + (i.requested_quantity || 0),
    0
  );
  const isInProgress =
    task.status === "in_progress" || task.status === "assigned";

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-medium">
                {sr?.client?.name ?? "Cliente desconocido"}
              </span>
              {sr?.delivery_date && (
                <Badge variant="outline" className="text-xs">
                  {dayjs(sr.delivery_date).format("DD/MM/YYYY")}
                </Badge>
              )}
              <Badge
                className={
                  task.status === "in_progress"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-blue-100 text-blue-700"
                }
              >
                {task.status === "in_progress" ? "En progreso" : "Asignado"}
              </Badge>
            </div>
          </div>
          {isInProgress && (
            <Button size="sm" onClick={onStart}>
              <Package className="mr-1.5 h-4 w-4" />
              {task.status === "in_progress" ? "Continuar" : "Iniciar"}
            </Button>
          )}
        </div>

        {items.length > 0 && (
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-7 w-full justify-start gap-1.5 px-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {open ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                {items.length} producto{items.length !== 1 ? "s" : ""} ·{" "}
                {totalBoxes} cajas
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-1.5 space-y-1 rounded-md border bg-muted/30 p-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <span className="min-w-0 flex-1 truncate text-foreground">
                      {item.product?.short_name ?? "Producto"}
                    </span>
                    <span className="shrink-0 font-medium text-muted-foreground">
                      {item.requested_quantity} cajas
                    </span>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}

function BodegaPickingView({ userId }: { userId: string }) {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["worker-pick-tasks", userId],
    queryFn: () => pickingService.getWorkerPickTasks(userId),
    refetchInterval: 30_000,
  });

  const myTasks = (data?.myTasks ?? []) as WorkerTask[];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
          <p className="text-red-600">Error al cargar tareas</p>
        </CardContent>
      </Card>
    );
  }

  if (!myTasks.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Package className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">
            No tienes tareas de picking asignadas
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Un encargado debe asignarte una tarea
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {myTasks.map((task) => (
        <BodegaTaskCard
          key={task.id}
          task={task}
          onStart={() => navigate(`/wms/picking/${task.id}`)}
        />
      ))}
    </div>
  );
}

// ─── Manager orders view ──────────────────────────────────────────────────────

function ManagerOrdersView({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: clientGroups,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["picking-orders"],
    queryFn: () => pickingService.getOrdersReadyForPicking(),
    refetchInterval: 30000,
  });

  const startPickingMutation = useMutation({
    mutationFn: (salesRequestId: string) =>
      pickingService.createPickTaskFromOrder(salesRequestId, userId),
    onSuccess: (pickTask) => {
      toast({
        title: "Tarea de picking creada",
        description: "Redirigiendo al flujo de picking...",
      });
      queryClient.invalidateQueries({ queryKey: ["picking-orders"] });
      queryClient.invalidateQueries({ queryKey: ["picking-tasks-assignment"] });
      navigate(`/wms/picking/${pickTask.id}`);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const filteredGroups = clientGroups
    ?.map((group) => ({
      ...group,
      orders: group.orders.filter((o) => o.pick_task_status !== "completed"),
    }))
    .filter(
      (group) =>
        group.orders.length > 0 &&
        group.client_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const activeGroups = clientGroups?.map((g) => ({
    ...g,
    orders: g.orders.filter((o) => o.pick_task_status !== "completed"),
  }));
  const totalOrders =
    activeGroups?.reduce((sum, g) => sum + g.orders.length, 0) || 0;
  const ordersWithStock =
    activeGroups?.reduce(
      (sum, g) =>
        sum +
        g.orders.filter((o) => o.stock_available && !o.pick_task_id).length,
      0
    ) || 0;
  const ordersInProgress =
    activeGroups?.reduce(
      (sum, g) =>
        sum +
        g.orders.filter(
          (o) =>
            o.pick_task_status === "assigned" ||
            o.pick_task_status === "in_progress"
        ).length,
      0
    ) || 0;

  const handleStartPicking = (orderId: string) => {
    startPickingMutation.mutate(orderId);
  };

  const handleContinuePicking = (pickTaskId: string) => {
    navigate(`/wms/picking/${pickTaskId}`);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card>
          <CardContent className="px-3 pb-3 pt-3 sm:px-6 sm:pb-4 sm:pt-4">
            <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-2">
              <FileText className="hidden h-4 w-4 text-muted-foreground sm:block" />
              <span className="text-center text-xs text-muted-foreground sm:text-left sm:text-sm">
                Total Pedidos
              </span>
            </div>
            <p className="mt-1 text-center text-xl font-bold sm:text-left sm:text-2xl">
              {totalOrders}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="px-3 pb-3 pt-3 sm:px-6 sm:pb-4 sm:pt-4">
            <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-2">
              <Boxes className="hidden h-4 w-4 text-green-500 sm:block" />
              <span className="text-center text-xs text-muted-foreground sm:text-left sm:text-sm">
                Con Stock
              </span>
            </div>
            <p className="mt-1 text-center text-xl font-bold text-green-600 sm:text-left sm:text-2xl">
              {ordersWithStock}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="px-3 pb-3 pt-3 sm:px-6 sm:pb-4 sm:pt-4">
            <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-2">
              <Package className="hidden h-4 w-4 text-blue-500 sm:block" />
              <span className="text-center text-xs text-muted-foreground sm:text-left sm:text-sm">
                En Progreso
              </span>
            </div>
            <p className="mt-1 text-center text-xl font-bold text-blue-600 sm:text-left sm:text-2xl">
              {ordersInProgress}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar cliente..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
            <p className="text-red-600">Error al cargar pedidos</p>
          </CardContent>
        </Card>
      ) : !filteredGroups || filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">
              {searchQuery
                ? "No se encontraron clientes con ese nombre"
                : "No hay pedidos listos para picking"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {filteredGroups.map((client) => (
            <ClientGroup
              key={client.client_id}
              client={client}
              onStartPicking={handleStartPicking}
              onContinuePicking={handleContinuePicking}
              isStarting={startPickingMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page entry point ─────────────────────────────────────────────────────────

export function PickingOrdersPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const userId = useProfileStore((state) => state.id);
  const userRole = useProfileStore((state) => state.role);

  const isBodega = userRole === "bodega";

  if (!userId) {
    toast({
      title: "Error",
      description: "No se pudo identificar el usuario",
      variant: "destructive",
    });
    return null;
  }

  return (
    <PageContainer className="space-y-4 sm:space-y-6">
      <PageHeader
        title={isBodega ? "Mis Tareas de Picking" : "Pedidos Para Picking"}
        description={
          isBodega
            ? "Tus tareas asignadas de picking"
            : "Pedidos listos para facturar - selecciona uno para iniciar picking"
        }
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
      />

      {/* Bodega: show only assigned tasks */}
      {isBodega ? (
        <BodegaPickingView userId={userId} />
      ) : (
        /* Manager: tabs with orders + assignment panel */
        <Tabs defaultValue="pedidos">
          <TabsList className="mb-4">
            <TabsTrigger value="pedidos" className="gap-1.5">
              <Package className="h-4 w-4" />
              Pedidos
            </TabsTrigger>
            <TabsTrigger value="asignacion" className="gap-1.5">
              <ClipboardList className="h-4 w-4" />
              Asignación de Tareas
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pedidos">
            <ManagerOrdersView userId={userId} />
          </TabsContent>
          <TabsContent value="asignacion">
            <PickingTaskAssignmentPanel />
          </TabsContent>
        </Tabs>
      )}
    </PageContainer>
  );
}
